import React, { useState } from 'react';
import { Button, MenuItem, Menu } from '@material-ui/core';
import KeyboardArrowDownOutlined from '@mui/icons-material/KeyboardArrowDownOutlined';
import { useSelector } from 'react-redux';

import { Invoice, InvoiceStatus } from 'generated/graphql';
import TickSquareSuccess from 'assets/icons/tick-square.svg';
import InfoSquareRed from 'assets/icons/info-square-red.svg';
import TimeCirclePending from 'assets/icons/time-circle-pending.svg';
import Img from 'retired/Img';
import Status from 'retired/Status';
import {
  statusError,
  statusNeutral,
  statusSuccess,
  statusTextError,
  statusTextNeutral,
  statusTextSuccess,
} from 'utils/color-styles';
import { selectUser } from 'entities/user/selectors';
import { Privileges } from 'utils/privileges';

export const INVOICE_STATUS = {
  [InvoiceStatus.Draft]: {
    name: 'Draft',
    icon: TimeCirclePending,
    chipColor: statusNeutral,
    textColor: statusTextNeutral,
    copy: 'The invoice is in the draft state and not visible to the client',
    availableTransitions: [InvoiceStatus.Sent, InvoiceStatus.Voided],
  },
  [InvoiceStatus.Sent]: {
    name: 'Sent',
    icon: TimeCirclePending,
    chipColor: statusNeutral,
    textColor: statusTextNeutral,
    copy: 'The invoice has been sent and is accessible by the client',
    availableTransitions: [InvoiceStatus.Paid, InvoiceStatus.Voided],
  },
  [InvoiceStatus.Paid]: {
    name: 'Paid',
    icon: TickSquareSuccess,
    chipColor: statusSuccess,
    textColor: statusTextSuccess,
    copy: 'The invoice has been paid',
    availableTransitions: [],
  },
  [InvoiceStatus.Voided]: {
    name: 'Voided',
    icon: InfoSquareRed,
    chipColor: statusError,
    textColor: statusTextError,
    copy: 'The invoice has been voided and is no longer visible to the client',
    availableTransitions: [],
  },
};

export const InvoiceStatusChip = ({
  invoice,
  onChange,
}: {
  invoice: Pick<Invoice, 'status'>;
  onChange: (value: InvoiceStatus) => void;
}): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<null | Element>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.SyntheticEvent<Element>): void => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const user = useSelector(selectUser);

  return user?.hasPrivilege(Privileges.RESTRICTED_INVOICING_MANAGE) &&
    INVOICE_STATUS[invoice.status].availableTransitions.length > 0 ? (
    <>
      <Button
        style={{
          color: INVOICE_STATUS[invoice.status].textColor,
          backgroundColor: INVOICE_STATUS[invoice.status].chipColor,
          zIndex: 1,
          marginLeft: 15,
          width: 'max-content',
          opacity: open ? '0.7' : '1',
        }}
        variant="contained"
        disableElevation
        disableRipple
        onClick={handleClick}
        endIcon={<KeyboardArrowDownOutlined />}
      >
        <div>
          {INVOICE_STATUS[invoice.status].name}
          <Img style={{ marginLeft: 20 }} src={INVOICE_STATUS[invoice.status].icon} alt="icon" />
        </div>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        style={{ marginTop: 50 }}
        marginThreshold={50}
      >
        {(Object.keys(INVOICE_STATUS) as InvoiceStatus[]).map((status) => (
          <MenuItem
            key={status}
            value={status}
            onClick={(): void => {
              handleClose();
              onChange(status);
            }}
            disabled={!INVOICE_STATUS[invoice.status].availableTransitions.includes(status)}
          >
            {INVOICE_STATUS[status].name}{' '}
          </MenuItem>
        ))}
      </Menu>
    </>
  ) : (
    <Status
      title={INVOICE_STATUS[invoice.status].name}
      icon={INVOICE_STATUS[invoice.status].icon}
      color={INVOICE_STATUS[invoice.status].chipColor}
      textColor={INVOICE_STATUS[invoice.status].textColor}
      style={{ width: 'max-content' }}
    />
  );
};
